import React, { useEffect, useState, useRef } from 'react';
import { UserRole, ProfileType, DateFilter } from '../types/commission';
import { Filter, Calendar, ChevronDown, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface FilterBarProps {
  role: UserRole;
  selectedProfiles: ProfileType[];
  onProfileChange: (profiles: ProfileType[]) => void;
  dateFilter: DateFilter;
  onDateFilterChange: (filter: DateFilter) => void;
}
export function FilterBar({
  role,
  selectedProfiles,
  onProfileChange,
  dateFilter,
  onDateFilterChange
}: FilterBarProps) {
  const isAdmin = role === 'System Administrator';
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleProfileToggle = (profile: ProfileType) => {
    if (selectedProfiles.includes(profile)) {
      onProfileChange(selectedProfiles.filter(p => p !== profile));
    } else {
      onProfileChange([...selectedProfiles, profile]);
    }
  };
  const clearFilters = () => {
    onProfileChange([]);
    onDateFilterChange('All');
  };
  const dateFilters: DateFilter[] = ['This Month', 'Last Month', 'Last 3 Months', 'Last 6 Months', 'YTD', 'All'];
  const profileTypes: ProfileType[] = ['Loan Officer', 'Sales Team Leader', 'Division Leader'];
  return <div className="w-full bg-white border-b border-slate-200 px-8 py-4 space-y-4" data-id="element-92">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-93">
        {/* Left Side: Filters */}
        <div className="flex flex-wrap items-center gap-4" data-id="element-94">
          {/* Profile Filter */}
          <div className="relative" ref={dropdownRef} data-id="element-95">
            {isAdmin ? <>
                <button onClick={() => setIsProfileOpen(!isProfileOpen)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 ${isProfileOpen || selectedProfiles.length > 0 ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-96">
                  <Filter size={16} data-id="element-97" />
                  <span className="text-sm font-medium" data-id="element-98">
                    {selectedProfiles.length === 0 ? 'All Profiles' : selectedProfiles.length === profileTypes.length ? 'All Profiles' : `${selectedProfiles.length} Selected`}
                  </span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} data-id="element-99" />
                </button>

                <AnimatePresence data-id="element-100">
                  {isProfileOpen && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 8
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-slate-100 py-2 z-50" data-id="element-101">
                      <div className="px-3 py-2 border-b border-slate-50 mb-1" data-id="element-102">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-103">
                          Filter by Profile
                        </span>
                      </div>
                      {profileTypes.map(profile => <label key={profile} className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-104">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedProfiles.includes(profile) ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`} data-id="element-105">
                            {selectedProfiles.includes(profile) && <Check size={10} className="text-white" data-id="element-106" />}
                          </div>
                          <input type="checkbox" className="hidden" checked={selectedProfiles.includes(profile)} onChange={() => handleProfileToggle(profile)} data-id="element-107" />
                          <span className="text-sm text-slate-700" data-id="element-108">
                            {profile}
                          </span>
                        </label>)}
                    </motion.div>}
                </AnimatePresence>
              </> : <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-slate-500 cursor-default" data-id="element-109">
                <Filter size={16} data-id="element-110" />
                <span className="text-sm font-medium" data-id="element-111">
                  Viewing: Your Profile
                </span>
              </div>}
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-slate-200 hidden sm:block" data-id="element-112" />

          {/* Date Segmented Control */}
          <div className="bg-slate-100 p-1 rounded-lg flex items-center overflow-x-auto no-scrollbar" data-id="element-113">
            {dateFilters.map(filter => <button key={filter} onClick={() => onDateFilterChange(filter)} className={`relative px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${dateFilter === filter ? 'text-blue-700' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-114">
                {dateFilter === filter && <motion.div layoutId="activeDatePill" className="absolute inset-0 bg-white shadow-sm rounded-md border border-slate-200/50" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-115" />}
                <span className="relative z-10" data-id="element-116">{filter}</span>
              </button>)}
          </div>
        </div>

        {/* Right Side: Clear Filters */}
        {(selectedProfiles.length > 0 && selectedProfiles.length < profileTypes.length || dateFilter !== 'All') && isAdmin && <button onClick={clearFilters} className="text-sm text-slate-500 hover:text-slate-800 font-medium transition-colors" data-id="element-117">
              Clear filters
            </button>}
      </div>

      {/* Active Filter Chips (Admin Only) */}
      {isAdmin && selectedProfiles.length > 0 && selectedProfiles.length < profileTypes.length && <div className="flex flex-wrap gap-2 pt-2" data-id="element-118">
            {selectedProfiles.map(profile => <motion.span key={profile} layout initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-119">
                {profile}
                <button onClick={() => handleProfileToggle(profile)} className="hover:bg-blue-100 rounded-full p-0.5 transition-colors" data-id="element-120">
                  <X size={12} data-id="element-121" />
                </button>
              </motion.span>)}
          </div>}
    </div>;
}